import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { footballAPI } from '../api/api';
import MobileHeader from '../components/MobileHeader';
import NewsSection from '../components/NewsSection';
import { useTranslations } from '../context/TranslationsContext';
import { useTheme } from '../context/ThemeContext';
import {
  MapPin, Calendar, Trophy, Users, ChevronLeft,
  TrendingUp, ArrowLeftRight, Newspaper, Target, Shield,
  Star, Zap, ArrowUpRight, ArrowDownLeft, X
} from 'lucide-react';

// ─── Country → Flag emoji map ─────────────────────────
const FLAG = {
  'Spain':'🇪🇸','France':'🇫🇷','Germany':'🇩🇪','Italy':'🇮🇹','Portugal':'🇵🇹',
  'Argentina':'🇦🇷','Brazil':'🇧🇷','England':'🏴󠁧󠁢󠁥󠁮󠁧󠁿','Netherlands':'🇳🇱','Belgium':'🇧🇪',
  'Croatia':'🇭🇷','Uruguay':'🇺🇾','Poland':'🇵🇱','Morocco':'🇲🇦','Senegal':'🇸🇳',
  'Egypt':'🇪🇬','Algeria':'🇩🇿','Tunisia':'🇹🇳','Saudi Arabia':'🇸🇦','Qatar':'🇶🇦',
  'United Arab Emirates':'🇦🇪','Kuwait':'🇰🇼','Bahrain':'🇧🇭','Oman':'🇴🇲',
  'Denmark':'🇩🇰','Austria':'🇦🇹','Switzerland':'🇨🇭','Serbia':'🇷🇸',
  'Scotland':'🏴󠁧󠁢󠁳󠁣󠁴󠁿','Wales':'🏴󠁧󠁢󠁷󠁬󠁳󠁿','Colombia':'🇨🇴','Mexico':'🇲🇽',
  'United States':'🇺🇸','Canada':'🇨🇦','Japan':'🇯🇵','South Korea':'🇰🇷',
  'Nigeria':'🇳🇬','Ghana':'🇬🇭','Ivory Coast':'🇨🇮','Cameroon':'🇨🇲',
  'Turkey':'🇹🇷','Greece':'🇬🇷','Czech Republic':'🇨🇿','Slovakia':'🇸🇰',
  'Hungary':'🇭🇺','Romania':'🇷🇴','Russia':'🇷🇺','Ukraine':'🇺🇦',
  'Sweden':'🇸🇪','Norway':'🇳🇴','Finland':'🇫🇮','Ireland':'🇮🇪',
  'Chile':'🇨🇱','Peru':'🇵🇪','Ecuador':'🇪🇨','Paraguay':'🇵🇾',
  'Bolivia':'🇧🇴','Venezuela':'🇻🇪','Jamaica':'🇯🇲','Costa Rica':'🇨🇷',
  'Iran':'🇮🇷','Iraq':'🇮🇶','Jordan':'🇯🇴','Syria':'🇸🇾','Lebanon':'🇱🇧',
  'Australia':'🇦🇺','China':'🇨🇳','Indonesia':'🇮🇩','Thailand':'🇹🇭',
  'Libya':'🇱🇾','Sudan':'🇸🇩','Palestine':'🇵🇸','Yemen':'🇾🇪',
  'New Zealand':'🇳🇿','Bulgaria':'🇧🇬','Albania':'🇦🇱','North Macedonia':'🇲🇰',
  'Bosnia and Herzegovina':'🇧🇦','Montenegro':'🇲🇪','Slovenia':'🇸🇮',
  'DR Congo':'🇨🇩','Mali':'🇲🇱','Guinea':'🇬🇳','Gabon':'🇬🇦',
  'South Africa':'🇿🇦','Kenya':'🇰🇪','Tanzania':'🇹🇿','Ethiopia':'🇪🇹',
  'Zimbabwe':'🇿🇼','Zambia':'🇿🇲','Angola':'🇦🇴','Mozambique':'🇲🇿',
};
const getFlag = (nationality) => FLAG[nationality] || '';

// ─── Helpers ──────────────────────────────────────────
const isFinished = s => ['FT','AET','PEN','AWD','WO'].includes(s);
const isLive     = s => ['1H','2H','HT','ET','BT','P'].includes(s);

const POS_COLOR = {
  Goalkeeper: { bg: 'rgba(168,85,247,0.15)', text: '#a855f7', label: 'GK',  border: '#a855f730' },
  Defender:   { bg: 'rgba(59,130,246,0.15)',  text: '#3b82f6', label: 'DEF', border: '#3b82f630' },
  Midfielder: { bg: 'rgba(34,197,94,0.15)',   text: '#22c55e', label: 'MID', border: '#22c55e30' },
  Attacker:   { bg: 'rgba(239,68,68,0.15)',   text: '#ef4444', label: 'FWD', border: '#ef444430' },
};
const POS_ORDER = ['Goalkeeper','Defender','Midfielder','Attacker'];
const POS_LABEL = { Goalkeeper: 'الحراسة', Defender: 'الدفاع', Midfielder: 'الوسط', Attacker: 'الهجوم' };
const RESULT_STYLE = {
  W: { bg: '#22c55e', label: 'ف', text: '#fff' },
  D: { bg: '#eab308', label: 'ت', text: '#0f172a' },
  L: { bg: '#ef4444', label: 'خ', text: '#fff' },
  U: { bg: '#374151', label: '-', text: '#fff' },
};

function getResult(m, teamId) {
  const isHome = m.home?.id === teamId || m.home?.id === Number(teamId);
  if (!isFinished(m.status?.short)) return 'U';
  const myG = isHome ? (m.goals?.home ?? 0) : (m.goals?.away ?? 0);
  const opG = isHome ? (m.goals?.away ?? 0) : (m.goals?.home ?? 0);
  return myG > opG ? 'W' : myG < opG ? 'L' : 'D';
}

const FAV_KEY = 'rtm_favorites';
function getLocalFavs() {
  try { return JSON.parse(localStorage.getItem(FAV_KEY)) || { teams: [], leagues: [] }; }
  catch { return { teams: [], leagues: [] }; }
}

// ─── Main Component ───────────────────────────────────
export default function TeamProfileGlobal() {
  const { teamId }  = useParams();
  const navigate    = useNavigate();
  const { translateTeam, translatePlayer } = useTranslations();
  const { isDark }  = useTheme();

  const [team,        setTeam]        = useState(null);
  const [transfers,   setTransfers]   = useState(null);
  const [news,        setNews]        = useState(null);
  const [squadStats,  setSquadStats]  = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [activeTab,   setActiveTab]   = useState('squad');
  const [isFav,       setIsFav]       = useState(() => {
    const favs = getLocalFavs();
    return (favs.teams || []).some(t => String(t.id || t) === String(teamId));
  });
  const [favAnim,     setFavAnim]     = useState(false);

  const toggleFav = useCallback(async () => {
    if (!team) return;
    const favs  = getLocalFavs();
    const teams = favs.teams || [];
    const idx   = teams.findIndex(t => String(t.id || t) === String(team.id));
    let newTeams;
    if (idx >= 0) {
      newTeams = teams.filter((_, i) => i !== idx);
    } else {
      newTeams = [...teams, { id: team.id, name: team.name, logo: team.logo || '' }];
    }
    const newFavs = { ...favs, teams: newTeams };
    localStorage.setItem(FAV_KEY, JSON.stringify(newFavs));
    window.dispatchEvent(new StorageEvent('storage', { key: FAV_KEY }));
    setIsFav(idx < 0);
    setFavAnim(true);
    setTimeout(() => setFavAnim(false), 600);

    // مزامنة السحابة
    try {
      const { syncFavsToCloud } = await import('../utils/notifSync');
      await syncFavsToCloud(newFavs);
    } catch {}

    // Toast
    const { toast } = await import('sonner');
    if (idx >= 0) {
      toast(`تمت الإزالة من المفضلة`);
    } else {
      toast.success(`${translateTeam(team.id, team.name)} في المفضلة ✓`);
    }
  }, [team, translateTeam]);

  const bg    = isDark ? '#030712' : '#f1f5f9';
  const bgCard = isDark ? 'rgba(255,255,255,0.03)' : '#ffffff';
  const border = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  const textPri = isDark ? '#ffffff' : '#0f172a';
  const textSec = isDark ? '#9ca3af' : '#64748b';
  const textMut = isDark ? '#4b5563' : '#94a3b8';

  useEffect(() => {
    footballAPI.getTeam(teamId)
      .then(r => setTeam(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [teamId]);

  // Lazy-load squad stats when squad tab is active
  useEffect(() => {
    if (activeTab !== 'squad' || squadStats !== null) return;
    const API = window.location.origin;
    fetch(`${API}/api/football/team/${teamId}/squad-stats`)
      .then(r => r.json())
      .then(d => setSquadStats(d.stats || {}))
      .catch(() => setSquadStats({}));
  }, [activeTab, teamId, squadStats]);

  // Lazy-load transfers on tab switch
  useEffect(() => {
    if (activeTab !== 'transfers' || transfers !== null) return;
    const API = window.location.origin;
    fetch(`${API}/api/football/team/${teamId}/transfers`)
      .then(r => r.json())
      .then(d => setTransfers(d.transfers || []))
      .catch(() => setTransfers([]));
  }, [activeTab, teamId, transfers]);

  // News is now handled by NewsSection component (lazy-loaded on mount)

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: bg, fontFamily: 'Tajawal, sans-serif' }}>
      <div className="relative">
        <div className="w-14 h-14 rounded-full border-2 border-yellow-500/20 border-t-yellow-500 animate-spin" />
        <Users className="absolute inset-0 m-auto w-5 h-5 text-yellow-500" />
      </div>
      <p className="text-sm" style={{ color: textMut }}>جاري تحميل بيانات الفريق...</p>
    </div>
  );

  if (!team) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3" style={{ background: bg, fontFamily: 'Tajawal, sans-serif' }}>
      <p className="font-bold" style={{ color: textPri }}>البيانات غير متوفرة مؤقتاً</p>
      <button onClick={() => navigate(-1)} className="px-4 py-2 rounded-xl text-sm font-bold text-yellow-500"
        style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.2)' }}>العودة</button>
    </div>
  );

  const teamName  = translateTeam(team.id, team.name);
  const completed = team.completed_fixtures || [];
  const upcoming  = team.upcoming_fixtures  || [];
  const squad     = team.squad || [];
  const form      = completed.slice(0, 5).map(m => getResult(m, team.id));

  const TABS = [
    { key: 'squad',     label: 'الفريق',      icon: Users },
    { key: 'results',   label: 'المباريات',   icon: Trophy },
    { key: 'stats',     label: 'الإحصائيات', icon: TrendingUp },
    { key: 'transfers', label: 'الانتقالات',  icon: ArrowLeftRight },
    { key: 'news',      label: 'الأخبار',     icon: Newspaper },
  ];

  return (
    <div className="min-h-screen" style={{ background: bg, fontFamily: 'Tajawal, sans-serif' }} data-testid="team-profile">
      <Helmet>
        <title>{teamName ? `${teamName} | فولتاسكور` : 'فولتاسكور'}</title>
        <meta name="description" content={
          teamName
            ? `تابع فريق ${teamName} على فولتاسكور — نتائج المباريات، إحصاءات اللاعبين، الانتقالات وآخر الأخبار.${team.country ? ` فريق من ${team.country}.` : ''}${squad.length > 0 ? ` ${squad.length} لاعباً في الفريق.` : ''}${completed.length > 0 ? ` ${completed.length} مباراة محللة.` : ''}`
            : ''
        } />
        <link rel="canonical" href={`https://voltascore.com/global/team/${teamId}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://voltascore.com/global/team/${teamId}`} />
        <meta property="og:title" content={teamName ? `${teamName} | فولتاسكور` : 'فولتاسكور'} />
        <meta property="og:description" content={
          teamName
            ? `إحصاءات ونتائج فريق ${teamName}${team.venue_name ? ` — ملعب ${team.venue_name}` : ''} على فولتاسكور`
            : ''
        } />
        {team.logo && <meta property="og:image" content={team.logo} />}
      </Helmet>
      <MobileHeader title={teamName} showBack />

      {/* ── Hero ── */}
      <div className="relative overflow-hidden">
        {team.venue_image
          ? <img src={team.venue_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
          : <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg,#0c1a2e,#030712)' }} />}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom,transparent 30%,#030712 100%)' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 0%,rgba(234,179,8,0.07),transparent)' }} />

        <div className="relative max-w-2xl mx-auto px-4 pt-5 pb-4">
          <div className="flex items-start gap-4">
            <div className="relative shrink-0">
              <div className="absolute inset-0 rounded-2xl blur-xl opacity-30" style={{ background: '#eab308' }} />
              <div className="relative w-20 h-20 rounded-2xl flex items-center justify-center p-2.5"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)' }}>
                <img src={team.logo} alt="" className="w-full h-full object-contain drop-shadow-xl" />
              </div>
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <div className="flex items-start gap-2">
                <h1 className="text-[22px] font-black text-white leading-tight mb-1 flex-1">{teamName}</h1>
                <button
                  onClick={toggleFav}
                  data-testid="team-fav-btn"
                  className="shrink-0 mt-1 w-9 h-9 rounded-xl flex items-center justify-center transition-all active:scale-90"
                  style={{
                    background: isFav ? 'rgba(234,179,8,0.15)' : 'rgba(255,255,255,0.06)',
                    border: isFav ? '1px solid rgba(234,179,8,0.35)' : '1px solid rgba(255,255,255,0.10)',
                    transform: favAnim ? 'scale(1.25)' : 'scale(1)',
                    transition: 'all 0.3s cubic-bezier(.22,.68,0,1.4)',
                  }}
                  title={isFav ? 'إزالة من المفضلة' : 'إضافة للمفضلة'}
                >
                  <Star
                    className="w-4 h-4 transition-all"
                    style={{ color: isFav ? '#eab308' : '#6b7280', fill: isFav ? '#eab308' : 'none', strokeWidth: 2 }}
                  />
                </button>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                {team.country && <span className="text-gray-500 text-[11px] flex items-center gap-1"><MapPin className="w-3 h-3" />{team.country}</span>}
                {team.founded && <span className="text-gray-500 text-[11px] flex items-center gap-1"><Calendar className="w-3 h-3" />تأسس {team.founded}</span>}
              </div>
              {team.venue_name && (
                <p className="text-gray-600 text-[11px] mt-1 truncate">
                  🏟 {team.venue_name}{team.venue_capacity && <span className="text-gray-700"> · {Number(team.venue_capacity).toLocaleString()} مقعد</span>}
                </p>
              )}
            </div>
          </div>

          {/* Form strip — Line style */}
          {form.length > 0 && (
            <div className="mt-4 flex items-center gap-3">
              <span className="text-gray-500 text-[9px] font-bold tracking-widest shrink-0">الشكل</span>
              <div className="flex items-end">
                {form.map((r, i) => {
                  const COLORS = { W: '#22c55e', D: '#eab308', L: '#ef4444', U: '#374151' };
                  const LABELS = { W: 'ف', D: 'ت', L: 'خ', U: '-' };
                  const c = COLORS[r];
                  const prevC = i > 0 ? COLORS[form[i - 1]] : c;
                  return (
                    <React.Fragment key={i}>
                      {i > 0 && (
                        <div style={{
                          width: 14, height: 2,
                          background: `linear-gradient(to right, ${prevC}, ${c})`,
                          marginBottom: 4, borderRadius: 0, flexShrink: 0
                        }} />
                      )}
                      <div className="flex flex-col items-center" style={{ gap: 3, animation: `popIn 0.35s cubic-bezier(.22,.68,0,1.4) ${i*70}ms both` }}>
                        <span style={{ fontSize: 9, fontWeight: 900, color: c, lineHeight: 1 }}>{LABELS[r]}</span>
                        <div style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: c,
                          boxShadow: `0 0 8px ${c}90, 0 0 2px ${c}`
                        }} />
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="sticky top-11 md:top-0 z-30 border-b" style={{ background: isDark ? 'rgba(3,7,18,0.96)' : 'rgba(241,245,249,0.96)', backdropFilter: 'blur(12px)', borderColor: border }}>
        <div className="max-w-2xl mx-auto px-3">
          <div className="flex overflow-x-auto gap-0.5 py-2" style={{ scrollbarWidth: 'none' }}>
            {TABS.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setActiveTab(key)} data-testid={`team-tab-${key}`}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-[12px] font-bold whitespace-nowrap transition-all shrink-0"
                style={activeTab === key
                  ? { background: 'rgba(234,179,8,0.1)', color: '#eab308', border: '1px solid rgba(234,179,8,0.2)' }
                  : { color: textMut, border: '1px solid transparent' }}>
                <Icon className="w-3.5 h-3.5" />{label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-2xl mx-auto px-4 mt-4 pb-28">
        {activeTab === 'squad'     && <SquadView squad={squad} squadStats={squadStats} translatePlayer={translatePlayer} isDark={isDark} bgCard={bgCard} border={border} textPri={textPri} textSec={textSec} />}
        {activeTab === 'results'   && <ResultsView completed={completed} upcoming={upcoming} teamId={team.id} navigate={navigate} translateTeam={translateTeam} isDark={isDark} bgCard={bgCard} border={border} textPri={textPri} textSec={textSec} textMut={textMut} />}
        {activeTab === 'stats'     && <StatsView completed={completed} upcoming={upcoming} teamId={team.id} squad={squad} isDark={isDark} bgCard={bgCard} border={border} textPri={textPri} textSec={textSec} textMut={textMut} />}
        {activeTab === 'transfers' && <TransfersView transfers={transfers} translatePlayer={translatePlayer} isDark={isDark} bgCard={bgCard} border={border} textPri={textPri} textSec={textSec} textMut={textMut} />}
        {activeTab === 'news'      && (
          <NewsSection
            query={translateTeam(team.id, team.name)}
            title={`أخبار ${translateTeam(team.id, team.name)}`}
            isDark={isDark}
            fullMode
          />
        )}
      </div>

      <style>{`
        @keyframes popIn  { from{opacity:0;transform:scale(0.4)} to{opacity:1;transform:scale(1)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}

// ─── Squad View (Position-grouped, statistical layout) ──
// ─── Player Quick-View Bottom Sheet ────────────────────
const POS_AR = { Goalkeeper: 'حارس مرمى', Defender: 'مدافع', Midfielder: 'وسط', Attacker: 'مهاجم', Forward: 'مهاجم' };
const POS_CLR = { Goalkeeper: '#a855f7', Defender: '#3b82f6', Midfielder: '#22c55e', Attacker: '#ef4444', Forward: '#ef4444' };

function PlayerSheet({ player, st, onClose, navigate, isDark }) {
  const posColor  = POS_CLR[player.position] || '#eab308';
  const posLabel  = POS_AR[player.position]  || player.position;
  const flag      = getFlag(st?.nationality || '');
  const age       = st?.age || player.age || null;
  const rating    = st?.rating ? parseFloat(st.rating).toFixed(1) : null;
  const apps      = st?.apps || 0;
  const goals     = st?.goals || 0;
  const assists   = st?.assists || 0;
  const yellow    = st?.yellow || 0;
  const red       = st?.red || 0;
  const bg        = isDark ? '#0d1829' : '#f1f5f9';
  const card      = isDark ? '#111e35' : '#ffffff';
  const textPri   = isDark ? '#f1f5f9' : '#0f172a';
  const textMut   = isDark ? '#4b5563' : '#94a3b8';
  const brd       = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)';

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 50, animation: 'fadeIn 0.2s ease' }} />

      {/* Sheet */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 51,
        background: bg, borderTopLeftRadius: 24, borderTopRightRadius: 24,
        borderTop: `1px solid ${brd}`, padding: '0 0 16px',
        animation: 'slideUp 0.32s cubic-bezier(0.32,0.72,0,1) both',
        maxHeight: '88vh', overflowY: 'auto', fontFamily: 'Tajawal, sans-serif',
      }}>
        {/* Handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: isDark ? '#1f2d42' : '#cbd5e1' }} />
        </div>

        {/* Close button */}
        <button onClick={onClose} style={{ position: 'absolute', top: 12, left: 16, padding: 6, borderRadius: 12, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', border: 'none', cursor: 'pointer', color: textMut }}>
          <X size={18} />
        </button>

        {/* ── Hero block ── */}
        <div style={{ padding: '8px 20px 20px', display: 'flex', gap: 16, alignItems: 'center', direction: 'rtl' }}>
          {/* Photo + number */}
          <div style={{ position: 'relative', shrink: 0 }}>
            <div style={{ width: 88, height: 88, borderRadius: 20, overflow: 'hidden', border: `2px solid ${posColor}40`, background: '#0a1628' }}>
              <img src={player.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
                onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(player.name)}&background=0a1628&color=eab308&size=128`; }} />
            </div>
            {player.number && (
              <div style={{ position: 'absolute', bottom: -6, right: -6, width: 24, height: 24, borderRadius: 8, background: posColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 900, color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                {player.number}
              </div>
            )}
          </div>

          {/* Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 20, background: `${posColor}20`, color: posColor }}>{posLabel}</span>
              {rating && <span style={{ fontSize: 11, fontWeight: 900, color: '#f97316' }}>⭐ {rating}</span>}
            </div>
            <p style={{ fontSize: 17, fontWeight: 900, color: textPri, margin: 0, lineHeight: 1.2 }}>{player.name}</p>
            {(st?.nationality || flag) && (
              <p style={{ fontSize: 11, color: textMut, margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                {flag && <span>{flag}</span>}
                {st?.nationality && <span>{st.nationality}</span>}
                {age && <span>· {age} سنة</span>}
              </p>
            )}
          </div>
        </div>

        {/* ── Stats grid ── */}
        <div style={{ margin: '0 16px 16px', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, direction: 'rtl' }}>
          {[
            { label: 'مباريات', val: apps,   color: textPri },
            { label: 'أهداف',   val: goals,  color: goals  > 0 ? '#eab308' : textPri },
            { label: 'تمريرات', val: assists, color: assists > 0 ? '#22c55e' : textPri },
            { label: 'صفراء',   val: yellow,  color: yellow > 0 ? '#eab308' : textPri },
            { label: 'حمراء',   val: red,     color: red    > 0 ? '#ef4444' : textPri },
          ].map(({ label, val, color }) => (
            <div key={label} style={{ background: card, borderRadius: 14, padding: '12px 6px', textAlign: 'center', border: `1px solid ${brd}` }}>
              <p style={{ fontSize: 18, fontWeight: 900, color, margin: 0 }}>{val}</p>
              <p style={{ fontSize: 9, color: textMut, margin: '2px 0 0' }}>{label}</p>
            </div>
          ))}
        </div>

        {/* ── CTA button ── */}
        <div style={{ padding: '0 16px 8px' }}>
          <button onClick={() => { onClose(); navigate(`/global/player/${player.id}`); }}
            style={{ width: '100%', padding: '14px', borderRadius: 14, background: posColor, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 800, color: '#fff', fontFamily: 'Tajawal, sans-serif', boxShadow: `0 6px 20px ${posColor}40`, transition: 'transform 0.15s', direction: 'rtl' }}>
            عرض الملف الكامل للاعب
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </>
  );
}

// ─── Stat columns definition ──────────────────────────
const STAT_COLS = [
  { key: 'red',     icon: <svg width="11" height="14" viewBox="0 0 12 18" fill="none"><rect x="1" y="1" width="10" height="16" rx="1.5" fill="#EF4444" stroke="#991B1B" strokeWidth="1"/></svg>, title: 'البطاقات الحمراء',  color: '#ef4444' },
  { key: 'yellow',  icon: <svg width="11" height="14" viewBox="0 0 12 18" fill="none"><rect x="1" y="1" width="10" height="16" rx="1.5" fill="#FCD34D" stroke="#D97706" strokeWidth="1"/></svg>, title: 'البطاقات الصفراء', color: '#eab308' },
  { key: 'assists', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="#22c55e" strokeWidth="1.5" fill="none"/><path d="M12 7 L12 12 L16 14" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="2" fill="#22c55e" opacity="0.4"/></svg>, title: 'تمريرات حاسمة',   color: '#22c55e' },
  { key: 'goals',   icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#1a1a2e" stroke="#eab308" strokeWidth="1.5"/><polygon points="12,8 14.5,9.5 13.5,12.5 10.5,12.5 9.5,9.5" fill="none" stroke="#eab308" strokeWidth="0.8"/><path d="M12 2 L12 5 M12 19 L12 22 M2 12 L5 12 M19 12 L22 12" stroke="#eab308" strokeWidth="1" strokeLinecap="round" opacity="0.6"/></svg>, title: 'أهداف',            color: '#eab308' },
  { key: 'lineups', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" stroke="#9ca3af" strokeWidth="1.5" fill="none"/><line x1="7" y1="8" x2="17" y2="8" stroke="#9ca3af" strokeWidth="1.2" strokeLinecap="round"/><line x1="7" y1="12" x2="14" y2="12" stroke="#9ca3af" strokeWidth="1.2" strokeLinecap="round"/><line x1="7" y1="16" x2="11" y2="16" stroke="#9ca3af" strokeWidth="1.2" strokeLinecap="round"/></svg>, title: 'التشكيلة',          color: '#6b7280' },
  { key: 'apps',    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M8 2 L8 6 L16 6 L16 2" stroke="#9ca3af" strokeWidth="1.2" fill="none"/><path d="M4 6 L4 22 L20 22 L20 6 Z" stroke="#9ca3af" strokeWidth="1.5" fill="none" strokeLinejoin="round"/><line x1="12" y1="6" x2="12" y2="14" stroke="#9ca3af" strokeWidth="1" opacity="0.5"/><circle cx="12" cy="17" r="1.5" fill="#9ca3af" opacity="0.6"/></svg>, title: 'مباريات',          color: '#6b7280' },
  { key: 'age',     icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="8" width="18" height="13" rx="3" stroke="#9ca3af" strokeWidth="1.5" fill="none"/><path d="M8 8 L8 5 Q12 2 16 5 L16 8" stroke="#9ca3af" strokeWidth="1.2" fill="none"/><line x1="12" y1="12" x2="12" y2="11" stroke="#eab308" strokeWidth="1.5" strokeLinecap="round"/><circle cx="12" cy="15" r="0.8" fill="#eab308"/></svg>, title: 'العمر',             color: '#6b7280' },
];

const COL_W = 26; // px per stat column

function SquadView({ squad, squadStats, translatePlayer, isDark, bgCard, border, textPri, textSec }) {
  const [posFilter,      setPosFilter]      = useState('all');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const navigate = useNavigate();
  const statsLoading = squadStats === null;

  const grouped = useMemo(() => {
    const g = {};
    POS_ORDER.forEach(p => { g[p] = []; });
    squad.forEach(p => { if (g[p.position]) g[p.position].push(p); });
    return g;
  }, [squad]);

  const filtered = posFilter === 'all' ? null : { [posFilter]: grouped[posFilter] || [] };
  const toRender = filtered || grouped;

  const rowBg    = isDark ? 'rgba(255,255,255,0.02)' : '#f8fafc';
  const rowBgAlt = isDark ? 'rgba(255,255,255,0.04)' : '#ffffff';
  const sep      = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)';
  const hdrBg    = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
  const textMuted = isDark ? '#4b5563' : '#94a3b8';

  return (
    <div data-testid="squad-view" style={{ fontFamily: 'Tajawal, sans-serif' }}>

      {/* Position filter pills */}
      <div className="flex gap-1.5 overflow-x-auto pb-3" style={{ scrollbarWidth: 'none' }}>
        {[
          { key: 'all',        label: 'الكل',                       color: '#eab308' },
          { key: 'Goalkeeper', label: `حراس (${grouped.Goalkeeper?.length || 0})`, color: '#a855f7' },
          { key: 'Defender',   label: `دفاع (${grouped.Defender?.length || 0})`,   color: '#3b82f6' },
          { key: 'Midfielder', label: `وسط (${grouped.Midfielder?.length || 0})`,  color: '#22c55e' },
          { key: 'Attacker',   label: `هجوم (${grouped.Attacker?.length || 0})`,   color: '#ef4444' },
        ].map(({ key, label, color }) => (
          <button key={key} onClick={() => setPosFilter(key)}
            className="px-3.5 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap transition-all border shrink-0"
            style={posFilter === key
              ? { background: color, color: '#fff', border: `1px solid ${color}`, boxShadow: `0 3px 12px ${color}40` }
              : { color: isDark ? '#6b7280' : '#94a3b8', border: `1px solid ${border}`, background: 'transparent' }}>
            {label}
          </button>
        ))}
      </div>

      {/* Position sections */}
      {POS_ORDER.filter(pos => toRender[pos]?.length > 0).map(pos => {
        const players = toRender[pos];
        const pc = POS_COLOR[pos];
        return (
          <div key={pos} className="mb-4 rounded-2xl overflow-hidden"
            style={{ border: `1px solid ${sep}`, animation: 'fadeUp 0.35s ease both' }}>

            {/* ── Section header row (icons) ── */}
            <div className="flex items-center px-3 py-2" style={{ background: hdrBg, direction: 'rtl' }}>
              {/* Position title */}
              <div className="flex items-center gap-1.5 flex-1">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: pc.text }} />
                <span className="text-[11px] font-black" style={{ color: pc.text }}>{POS_LABEL[pos]}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full ml-1" style={{ background: pc.bg, color: pc.text }}>{players.length}</span>
              </div>
              {/* Stat column icons */}
              <div className="flex items-center" style={{ gap: 0, direction: 'ltr' }}>
                {STAT_COLS.map(col => (
                  <div key={col.key} style={{ width: COL_W, textAlign: 'center' }}
                    title={col.title} className="text-[11px] leading-none">{col.icon}</div>
                ))}
              </div>
            </div>

            {/* ── Player rows ── */}
            {players.map((p, pi) => {
              const name = translatePlayer(p.id, p.name);
              const st   = (squadStats || {})[String(p.id)] || {};
              const flag = getFlag(st.nationality || '');
              // age: prefer from stats (nationality included), fallback to squad data
              const age  = st.age || p.age || null;
              const vals = {
                red:     st.red     ?? 0,
                yellow:  st.yellow  ?? 0,
                assists: st.assists ?? 0,
                goals:   st.goals   ?? 0,
                lineups: st.lineups ?? 0,
                apps:    st.apps    ?? 0,
                age:     age        ?? '-',
              };

              return (
                <div key={p.id}
                  onClick={() => setSelectedPlayer(p)}
                  data-testid={`squad-player-${p.id}`}
                  className="flex items-center px-2 py-2 transition-all cursor-pointer active:opacity-75"
                  style={{
                    background: pi % 2 === 0 ? rowBgAlt : rowBg,
                    borderTop: `1px solid ${sep}`,
                    direction: 'rtl',
                    userSelect: 'none',
                  }}>

                  {/* Player info – right side */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {/* Photo + number badge */}
                    <div className="relative shrink-0">
                      <div className="w-8 h-8 rounded-full overflow-hidden"
                        style={{ border: `2px solid ${pc.text}30` }}>
                        <img src={p.photo} alt="" className="w-full h-full object-cover object-top"
                          onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name)}&background=1a2535&color=fff&size=64`; }} />
                      </div>
                      {p.number && (
                        <div className="absolute -bottom-1 -right-1 rounded-full flex items-center justify-center text-[7px] font-black leading-none"
                          style={{ width: 16, height: 16, background: pc.text, color: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
                          {p.number}
                        </div>
                      )}
                    </div>

                    {/* Name + nationality */}
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold leading-tight truncate" style={{ color: textPri }}>{name}</p>
                      {(st.nationality || flag) && (
                        <p className="text-[10px] leading-tight mt-0.5 flex items-center gap-0.5 truncate" style={{ color: textMuted }}>
                          {flag && <span>{flag}</span>}
                          {st.nationality && <span>{st.nationality}</span>}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Stat values */}
                  <div className="flex items-center shrink-0" style={{ gap: 0, direction: 'ltr' }}>
                    {STAT_COLS.map(col => {
                      const v = vals[col.key];
                      const isZero = v === 0 || v === '0';
                      const isGoal = col.key === 'goals' && !isZero;
                      const isAssist = col.key === 'assists' && !isZero;
                      const isCard = (col.key === 'red' || col.key === 'yellow') && !isZero;
                      return (
                        <div key={col.key} style={{ width: COL_W, textAlign: 'center' }}>
                          {statsLoading ? (
                            <div className="mx-auto rounded" style={{ width: 16, height: 12, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }} />
                          ) : (
                            <span className="text-[11px] font-bold tabular-nums"
                              style={{
                                color: isGoal ? '#eab308' : isAssist ? '#22c55e' : isCard ? col.color : isZero ? textMuted : textPri,
                              }}>
                              {v}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {squad.length === 0 && (
        <div className="text-center py-14">
          <Users className="w-10 h-10 mx-auto mb-3" style={{ color: isDark ? '#1f2937' : '#cbd5e1' }} />
          <p className="text-sm" style={{ color: isDark ? '#4b5563' : '#94a3b8' }}>لا تتوفر بيانات الفريق</p>
        </div>
      )}

      {/* Bottom Sheet */}
      {selectedPlayer && (
        <PlayerSheet
          player={selectedPlayer}
          st={(squadStats || {})[String(selectedPlayer.id)] || {}}
          onClose={() => setSelectedPlayer(null)}
          navigate={navigate}
          isDark={isDark}
        />
      )}
    </div>
  );
}

// ─── Results View (Completed + Upcoming split, with round info) ──
function ResultsView({ completed, upcoming, teamId, navigate, translateTeam, isDark, bgCard, border, textPri, textSec, textMut }) {
  const [tab, setTab] = useState(upcoming.length > 0 ? 'upcoming' : 'completed');

  return (
    <div data-testid="results-view">
      {/* Sub-tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { key: 'upcoming',  label: 'القادمة',   count: upcoming.length  },
          { key: 'completed', label: 'المنهية',   count: completed.length },
        ].map(({ key, label, count }) => (
          <button key={key} onClick={() => setTab(key)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-[12px] font-bold transition-all"
            style={tab === key
              ? { background: 'rgba(234,179,8,0.1)', color: '#eab308', border: '1px solid rgba(234,179,8,0.2)' }
              : { color: textMut, border: `1px solid ${border}`, background: 'transparent' }}>
            {label}
            <span className="text-[10px] px-1.5 py-0.5 rounded-full"
              style={{ background: tab === key ? 'rgba(234,179,8,0.15)' : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'), color: tab === key ? '#eab308' : textMut }}>
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* Match list */}
      <div className="space-y-2">
        {(tab === 'upcoming' ? upcoming : completed).map((m, i) => (
          <MatchCard key={m.id || i} m={m} teamId={teamId} index={i} navigate={navigate}
            translateTeam={translateTeam} isDark={isDark} bgCard={bgCard} border={border}
            textPri={textPri} textSec={textSec} textMut={textMut} />
        ))}
        {(tab === 'upcoming' ? upcoming : completed).length === 0 && (
          <div className="text-center py-14">
            <Trophy className="w-10 h-10 mx-auto mb-3" style={{ color: isDark ? '#1f2937' : '#cbd5e1' }} />
            <p className="text-sm" style={{ color: textMut }}>لا توجد مباريات</p>
          </div>
        )}
      </div>
    </div>
  );
}

function MatchCard({ m, teamId, index, navigate, translateTeam, isDark, bgCard, border, textPri, textSec, textMut }) {
  const result  = getResult(m, teamId);
  const rs      = RESULT_STYLE[result];
  const live    = isLive(m.status?.short);
  const fin     = isFinished(m.status?.short);
  const isHome  = m.home?.id === teamId || m.home?.id === Number(teamId);
  const date    = m.date ? new Date(m.date) : null;
  const round   = m.league?.round ? m.league.round.replace('Regular Season - ', 'الجولة ').replace('Round of', 'دور الـ') : null;

  return (
    <button onClick={() => navigate(`/global/match/${m.id}`)} data-testid={`result-${m.id}`}
      className="w-full rounded-2xl overflow-hidden text-right transition-all active:scale-[0.99]"
      style={{ background: bgCard, border: `1px solid ${fin ? rs.bg + '25' : border}`, animation: `fadeUp 0.3s ease ${index * 35}ms both` }}>

      {/* League + round row */}
      <div className="flex items-center justify-between px-3.5 pt-2.5 pb-1.5 border-b" style={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)' }}>
        <div className="flex items-center gap-1.5 min-w-0">
          {m.league?.logo && <img src={m.league.logo} alt="" className="w-4 h-4 object-contain" />}
          <span className="text-[10px] truncate" style={{ color: textMut }}>{m.league?.name}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {round && <span className="text-[9.5px] font-semibold px-2 py-0.5 rounded-full" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: textSec }}>{round}</span>}
          {date && <span className="text-[10px]" style={{ color: textMut }}>{date.toLocaleDateString('ar-SA', { day: 'numeric', month: 'short' })}</span>}
          {live && <span className="text-[9px] font-bold text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded-full animate-pulse">مباشر</span>}
        </div>
      </div>

      {/* Score row */}
      <div className="flex items-center px-3.5 py-3 gap-3">
        {/* Home */}
        <div className={`flex-1 flex items-center justify-end gap-2 min-w-0 ${isHome ? '' : 'opacity-75'}`}>
          <span className="text-[11px] truncate font-semibold" style={{ color: m.home?.winner ? textPri : textMut }}>{translateTeam(m.home?.id, m.home?.name)}</span>
          <img src={m.home?.logo} alt="" className="w-8 h-8 object-contain rounded-lg shrink-0" style={{ background: 'white', padding: 2, boxShadow: '0 1px 6px rgba(0,0,0,0.2)' }} />
        </div>

        {/* Score / Time */}
        <div className="shrink-0 flex flex-col items-center gap-0.5 min-w-[56px]">
          {(fin || live) ? (
            <>
              <div className="flex items-center gap-2">
                <span className="text-[20px] font-black tabular-nums" style={{ color: m.home?.winner ? '#ffffff' : textMut }}>{m.goals?.home ?? '-'}</span>
                <span style={{ color: isDark ? '#374151' : '#cbd5e1' }}>–</span>
                <span className="text-[20px] font-black tabular-nums" style={{ color: m.away?.winner ? '#ffffff' : textMut }}>{m.goals?.away ?? '-'}</span>
              </div>
              {fin && (
                <div className="px-2.5 py-0.5 rounded-full text-[9px] font-black"
                  style={{ background: rs.bg + '20', color: rs.bg, border: `1px solid ${rs.bg}30` }}>
                  {result === 'W' ? 'فوز' : result === 'D' ? 'تعادل' : result === 'L' ? 'خسارة' : '-'}
                </div>
              )}
            </>
          ) : (
            <div className="text-center">
              <span className="text-[16px] font-black text-yellow-500 block">
                {date?.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit', hour12: true })}
              </span>
              <span className="text-[9px]" style={{ color: textMut }}>{date?.toLocaleDateString('ar-SA', { weekday: 'short' })}</span>
            </div>
          )}
        </div>

        {/* Away */}
        <div className={`flex-1 flex items-center gap-2 min-w-0 ${!isHome ? '' : 'opacity-75'}`}>
          <img src={m.away?.logo} alt="" className="w-8 h-8 object-contain rounded-lg shrink-0" style={{ background: 'white', padding: 2, boxShadow: '0 1px 6px rgba(0,0,0,0.2)' }} />
          <span className="text-[11px] truncate font-semibold" style={{ color: m.away?.winner ? textPri : textMut }}>{translateTeam(m.away?.id, m.away?.name)}</span>
        </div>
      </div>
    </button>
  );
}

// ─── Stats View ───────────────────────────────────────
function StatsView({ completed, teamId, squad, isDark, bgCard, border, textPri, textSec, textMut }) {
  const stats = useMemo(() => {
    const tid = Number(teamId);
    const played = completed.length;
    let wins = 0, draws = 0, losses = 0, gf = 0, ga = 0, cs = 0, homeW = 0, awayW = 0;

    completed.forEach(m => {
      const isHome = m.home?.id === tid;
      const myG  = isHome ? (m.goals?.home ?? 0) : (m.goals?.away ?? 0);
      const opG  = isHome ? (m.goals?.away ?? 0) : (m.goals?.home ?? 0);
      gf += myG; ga += opG;
      if (opG === 0) cs++;
      const r = getResult(m, tid);
      if (r === 'W') { wins++; if (isHome) homeW++; else awayW++; }
      else if (r === 'D') draws++;
      else if (r === 'L') losses++;
    });

    const winRate = played > 0 ? Math.round((wins / played) * 100) : 0;
    const avgGF   = played > 0 ? (gf / played).toFixed(1) : 0;
    const avgGA   = played > 0 ? (ga / played).toFixed(1) : 0;
    const csRate  = played > 0 ? Math.round((cs / played) * 100) : 0;

    return { played, wins, draws, losses, gf, ga, cs, homeW, awayW, winRate, avgGF, avgGA, csRate };
  }, [completed, teamId]);

  const posStats = useMemo(() => {
    const counts = { Goalkeeper: 0, Defender: 0, Midfielder: 0, Attacker: 0 };
    squad.forEach(p => { if (counts[p.position] !== undefined) counts[p.position]++; });
    return counts;
  }, [squad]);

  const StatCard = ({ label, value, sub, color = '#eab308', icon: Icon }) => (
    <div className="rounded-2xl p-4 flex flex-col gap-1" style={{ background: bgCard, border: `1px solid ${border}` }}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold" style={{ color: isDark ? '#6b7280' : '#94a3b8' }}>{label}</span>
        {Icon && <Icon className="w-4 h-4" style={{ color }} />}
      </div>
      <span className="text-[28px] font-black leading-none" style={{ color }}>{value}</span>
      {sub && <span className="text-[10px]" style={{ color: textMut }}>{sub}</span>}
    </div>
  );

  const BarStat = ({ label, value, max, color }) => {
    const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
    return (
      <div className="flex items-center gap-3">
        <span className="text-[11px] w-20 shrink-0 text-right" style={{ color: textSec }}>{label}</span>
        <div className="flex-1 h-2 rounded-full" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
        </div>
        <span className="text-[12px] font-black w-8 shrink-0" style={{ color: textPri }}>{value}</span>
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="stats-view">
      {/* Overview grid */}
      <div>
        <SectionTitle text="نظرة عامة" />
        <div className="grid grid-cols-2 gap-2.5">
          <StatCard label="المباريات" value={stats.played} sub={`${stats.wins} ف · ${stats.draws} ت · ${stats.losses} خ`} icon={Trophy} />
          <StatCard label="نسبة الفوز" value={`${stats.winRate}%`} sub="من المباريات المنهية" color="#22c55e" icon={Target} />
          <StatCard label="الأهداف المسجلة" value={stats.gf} sub={`معدل ${stats.avgGF} للمباراة`} color="#eab308" icon={Zap} />
          <StatCard label="الأهداف المستقبلة" value={stats.ga} sub={`معدل ${stats.avgGA} للمباراة`} color="#ef4444" icon={Shield} />
        </div>
      </div>

      {/* Win/Draw/Loss bars */}
      <div className="rounded-2xl p-4 space-y-3" style={{ background: bgCard, border: `1px solid ${border}` }}>
        <SectionTitle text="توزيع النتائج" small />
        <BarStat label="فوز"     value={stats.wins}   max={stats.played} color="#22c55e" />
        <BarStat label="تعادل"   value={stats.draws}  max={stats.played} color="#eab308" />
        <BarStat label="خسارة"   value={stats.losses} max={stats.played} color="#ef4444" />
      </div>

      {/* Home/Away split */}
      <div className="rounded-2xl p-4 space-y-3" style={{ background: bgCard, border: `1px solid ${border}` }}>
        <SectionTitle text="انتصارات بيت / خارجه" small />
        <BarStat label="بيت"     value={stats.homeW} max={stats.wins || 1} color="#3b82f6" />
        <BarStat label="خارجه"   value={stats.awayW} max={stats.wins || 1} color="#a855f7" />
      </div>

      {/* Clean sheets */}
      <div className="rounded-2xl p-4 flex items-center justify-between" style={{ background: bgCard, border: `1px solid ${border}` }}>
        <div>
          <p className="text-[10px] font-bold mb-0.5" style={{ color: isDark ? '#6b7280' : '#94a3b8' }}>الشباك النظيفة</p>
          <p className="text-[28px] font-black" style={{ color: '#3b82f6' }}>{stats.cs}</p>
          <p className="text-[10px]" style={{ color: textMut }}>{stats.csRate}% من المباريات</p>
        </div>
        <div className="relative w-16 h-16">
          <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'} strokeWidth="2.5" />
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#3b82f6" strokeWidth="2.5"
              strokeDasharray={`${stats.csRate} ${100 - stats.csRate}`} strokeLinecap="round" />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[11px] font-black" style={{ color: '#3b82f6' }}>{stats.csRate}%</span>
        </div>
      </div>

      {/* Squad composition */}
      {squad.length > 0 && (
        <div className="rounded-2xl p-4 space-y-3" style={{ background: bgCard, border: `1px solid ${border}` }}>
          <SectionTitle text="تركيبة الفريق" small />
          {POS_ORDER.map(pos => {
            const pc = POS_COLOR[pos];
            return (
              <BarStat key={pos} label={POS_LABEL[pos]} value={posStats[pos]} max={squad.length} color={pc.text} />
            );
          })}
          <p className="text-[10px] text-center pt-1" style={{ color: textMut }}>إجمالي {squad.length} لاعب</p>
        </div>
      )}
    </div>
  );
}

// ─── Transfers View ───────────────────────────────────
function TransfersView({ transfers, translatePlayer, isDark, bgCard, border, textPri, textSec, textMut }) {
  if (transfers === null) return (
    <div className="flex flex-col items-center justify-center gap-3 py-20">
      <div className="w-8 h-8 rounded-full border-2 border-yellow-500/20 border-t-yellow-500 animate-spin" />
      <p className="text-sm" style={{ color: textMut }}>جاري تحميل الانتقالات...</p>
    </div>
  );

  if (!transfers.length) return (
    <div className="text-center py-16">
      <ArrowLeftRight className="w-10 h-10 mx-auto mb-3" style={{ color: isDark ? '#1f2937' : '#cbd5e1' }} />
      <p className="text-sm" style={{ color: textMut }}>لا تتوفر بيانات الانتقالات</p>
    </div>
  );

  const TYPE_STYLE = {
    'Free':  { color: '#22c55e', label: 'مجاني' },
    'Loan':  { color: '#3b82f6', label: 'إعارة' },
    'N/A':   { color: '#6b7280', label: 'غير محدد' },
  };
  const getTypeStyle = (type) => {
    if (!type || type === 'N/A') return TYPE_STYLE['N/A'];
    if (type.toLowerCase().includes('free'))  return TYPE_STYLE['Free'];
    if (type.toLowerCase().includes('loan'))  return TYPE_STYLE['Loan'];
    return { color: '#eab308', label: type };
  };

  return (
    <div className="space-y-2" data-testid="transfers-view">
      <p className="text-[11px] mb-3" style={{ color: textMut }}>{transfers.length} انتقال مسجل</p>
      {transfers.map((t, i) => {
        const ts = getTypeStyle(t.type);
        const date = t.date ? new Date(t.date) : null;
        const playerName = translatePlayer(t.player_id, t.player_name);
        const isIn = t.to?.id !== undefined;

        return (
          <div key={i} className="rounded-xl p-3 flex items-center gap-3"
            style={{ background: bgCard, border: `1px solid ${border}`, animation: `fadeUp 0.3s ease ${i * 25}ms both` }}>
            {/* Player photo */}
            <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0" style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}>
              {t.player_photo
                ? <img src={t.player_photo} alt="" className="w-full h-full object-cover object-top" />
                : <div className="w-full h-full flex items-center justify-center text-[10px] font-bold" style={{ color: textMut }}>{playerName?.charAt(0)}</div>}
            </div>
            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-bold truncate" style={{ color: textPri }}>{playerName}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                {t.from?.logo && <img src={t.from.logo} alt="" className="w-3.5 h-3.5 object-contain" />}
                <span className="text-[10px]" style={{ color: textMut }}>{t.from?.name || '؟'}</span>
                <ArrowLeftRight className="w-3 h-3 shrink-0" style={{ color: textMut }} />
                {t.to?.logo && <img src={t.to.logo} alt="" className="w-3.5 h-3.5 object-contain" />}
                <span className="text-[10px]" style={{ color: textSec }}>{t.to?.name || '؟'}</span>
              </div>
            </div>
            {/* Right side */}
            <div className="text-left shrink-0">
              <div className="text-[9.5px] font-bold px-2 py-0.5 rounded-full mb-1"
                style={{ background: ts.color + '15', color: ts.color }}>{ts.label}</div>
              {date && <p className="text-[9px]" style={{ color: textMut }}>{date.toLocaleDateString('ar-SA', { year: 'numeric', month: 'short' })}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── News View ────────────────────────────────────────
function NewsView({ news, isDark, bgCard, border, textPri, textSec, textMut }) {
  if (news === null) return (
    <div className="flex flex-col items-center justify-center gap-3 py-20">
      <div className="w-8 h-8 rounded-full border-2 border-yellow-500/20 border-t-yellow-500 animate-spin" />
      <p className="text-sm" style={{ color: textMut }}>جاري تحميل الأخبار...</p>
    </div>
  );

  const articles = Array.isArray(news) ? news : [];

  if (!articles.length) return (
    <div className="text-center py-16">
      <Newspaper className="w-10 h-10 mx-auto mb-3" style={{ color: isDark ? '#1f2937' : '#cbd5e1' }} />
      <p className="text-sm" style={{ color: textMut }}>لا تتوفر أخبار حالياً</p>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="news-view">
      {articles.slice(0, 20).map((a, i) => {
        const article = a.article || a;
        const title   = article.title || article.headline || '';
        const source  = article.source?.name || article.sourceName || article.source || '';
        const img     = article.urlToImage || article.image || article.thumbnail || '';
        const url     = article.url || article.link || '#';
        const date    = article.publishedAt || article.pubDate || article.published || '';
        const dateStr = date ? new Date(date).toLocaleDateString('ar-SA', { day: 'numeric', month: 'short' }) : '';

        return (
          <a key={i} href={url} target="_blank" rel="noopener noreferrer"
            className="flex gap-3 rounded-xl p-3 transition-all active:scale-[0.99]"
            style={{ background: bgCard, border: `1px solid ${border}`, animation: `fadeUp 0.3s ease ${i * 30}ms both` }}>
            {img && (
              <div className="w-20 h-16 rounded-lg overflow-hidden shrink-0">
                <img src={img} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-bold leading-snug line-clamp-2 mb-1" style={{ color: textPri }}>{title}</p>
              <div className="flex items-center gap-1.5">
                {source && <span className="text-[10px] font-semibold" style={{ color: '#eab308' }}>{source}</span>}
                {dateStr && <span className="text-[9.5px]" style={{ color: textMut }}>· {dateStr}</span>}
              </div>
            </div>
          </a>
        );
      })}
    </div>
  );
}

// ─── Shared ───────────────────────────────────────────
function SectionTitle({ text, small }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="w-1 h-4 rounded-full bg-yellow-500" />
      <h3 className={`font-black ${small ? 'text-[11px]' : 'text-sm'} text-white`}>{text}</h3>
    </div>
  );
}
